import { useEffect, useState } from 'react';
import isEmpty from 'lodash/isEmpty';
import { ff } from '@atlassian/jira-feature-flagging';
import type { FieldConfig } from '@atlassian/jira-issue-create-common-types';
import { useTriggerIssueCreateModal } from '@atlassian/jira-packages-controllers-use-trigger-issue-create-modal';
import { prefillCustomFieldMap, getCascadingSelectFieldValue } from './prefill-custom-field-map';
import type { CascadingSelectFieldArrays } from './types';

export const usePrefillCustomFields = (modalFieldConfig: FieldConfig) => {
	const [{ prefilledCustomFields }] = useTriggerIssueCreateModal();
	const [data, setData] = useState(modalFieldConfig);

	useEffect(() => {
		if (
			!ff('connect-ecosystem-api-modern-issue-create_ccz1b') ||
			!prefilledCustomFields ||
			Object.keys(prefilledCustomFields).length === 0 ||
			isEmpty(modalFieldConfig)
		) {
			setData(modalFieldConfig);
			return;
		}

		const cascadingSelectFieldParentOptions: CascadingSelectFieldArrays = {};
		const cascadingSelectFieldChildOptions: CascadingSelectFieldArrays = {};
		const nextData = { ...modalFieldConfig };
		Object.keys(prefilledCustomFields).forEach((key) => {
			if (key in modalFieldConfig) {
				const prefilledDefaultValue = prefillCustomFieldMap({
					gqlType: modalFieldConfig[key].gqlType,
					defaultValue: prefilledCustomFields[key],
					allowedValues: modalFieldConfig[key]?.allowedValues,
				});
				nextData[key] = {
					...nextData[key],
					defaultValue: prefilledDefaultValue || modalFieldConfig[key].defaultValue,
				};
				if (modalFieldConfig[key].gqlType === 'JiraCascadingSelectField') {
					cascadingSelectFieldParentOptions[key] = prefilledCustomFields[key];
				}
			} else if (key.slice(-2) === ':1') {
				const fieldId = key.slice(0, -2);
				cascadingSelectFieldChildOptions[fieldId] = prefilledCustomFields[key];
			}
		});
		Object.keys(cascadingSelectFieldParentOptions).forEach((key) => {
			const parentOptionId = cascadingSelectFieldParentOptions[key];
			const childOptionId = cascadingSelectFieldChildOptions[key];
			const prefilledDefaultValue = getCascadingSelectFieldValue({
				parentOptionId,
				childOptionId,
				allowedValues: modalFieldConfig[key]?.allowedValues,
			});
			nextData[key] = {
				...nextData[key],
				defaultValue: prefilledDefaultValue || modalFieldConfig[key].defaultValue,
			};
		});

		setData(nextData);
	}, [modalFieldConfig, prefilledCustomFields]);

	return data;
};
